import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";

const COUNTRY_NAMES = new Intl.DisplayNames(["en"], { type: "region" });

const inputSchema = z.object({
  email: z
    .string()
    .trim()
    .email({ message: "Invalid email address" })
    .max(255, { message: "Email too long" }),
  siteLanguage: z.enum(["en", "fr"]).default("en"),
  browserLanguage: z.string().trim().max(80).optional().default(""),
  captchaToken: z.string().trim().max(4096).optional().default(""),
});

type Country = { name: string; code: string };

function countryFromCode(code: string | null): Country | null {
  const normalized = code?.trim().toUpperCase();
  if (!normalized || normalized === "XX" || !/^[A-Z]{2}$/.test(normalized)) return null;
  return {
    name: COUNTRY_NAMES.of(normalized) || "",
    code: normalized,
  };
}

function extractHeaderLanguage(): string {
  return getRequestHeader("accept-language")?.split(",")[0]?.trim() || "";
}

function countryFromBrowserLanguage(language: string): Country | null {
  const region = language
    .replace(/_/g, "-")
    .split("-")
    .slice(1)
    .find((part) => /^[a-z]{2}$/i.test(part));
  return countryFromCode(region || null);
}

async function lookupCountryFromIpApi(ip: string): Promise<Country | null> {
  const res = await fetch(`https://ipapi.co/${encodeURIComponent(ip)}/json/`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { country_name?: string; country_code?: string };
  const codeCountry = countryFromCode(data.country_code || null);
  return {
    name: typeof data.country_name === "string" ? data.country_name : codeCountry?.name || "",
    code: codeCountry?.code || "",
  };
}

async function lookupCountryFromIpWhoIs(ip: string): Promise<Country | null> {
  const res = await fetch(`https://ipwho.is/${encodeURIComponent(ip)}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { success?: boolean; country?: string; country_code?: string };
  if (data.success === false) return null;
  const codeCountry = countryFromCode(data.country_code || null);
  return {
    name: typeof data.country === "string" ? data.country : codeCountry?.name || "",
    code: codeCountry?.code || "",
  };
}

async function lookupCountry(ip: string | null): Promise<Country> {
  const cloudflareCountry = countryFromCode(getRequestHeader("cf-ipcountry") || null);
  if (cloudflareCountry) return cloudflareCountry;
  if (!ip) return { name: "", code: "" };
  const ipApiCountry = await lookupCountryFromIpApi(ip).catch(() => null);
  if (ipApiCountry?.name || ipApiCountry?.code) return ipApiCountry;
  const ipWhoIsCountry = await lookupCountryFromIpWhoIs(ip).catch(() => null);
  if (ipWhoIsCountry?.name || ipWhoIsCountry?.code) return ipWhoIsCountry;
  return { name: "", code: "" };
}

function extractIp(): string | null {
  const cf = getRequestHeader("cf-connecting-ip");
  if (cf) return cf;
  const xff = getRequestHeader("x-forwarded-for");
  if (xff) return xff.split(",")[0]?.trim() || null;
  const real = getRequestHeader("x-real-ip");
  if (real) return real;
  return null;
}

function isPrivateIp(ip: string): boolean {
  return (
    ip === "127.0.0.1" ||
    ip === "::1" ||
    ip.startsWith("10.") ||
    ip.startsWith("192.168.") ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(ip) ||
    ip.startsWith("fc") ||
    ip.startsWith("fd")
  );
}

// Stable per-email id so repeat signups upsert one OneSignal user (no duplicates).
async function emailExternalId(email: string): Promise<string> {
  const bytes = new TextEncoder().encode(email);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  return `waitlist_${hex}`;
}

// Upsert an email subscriber into the OneSignal app (production/preview chosen by
// env). notification_types: 1 = subscribed; a new email token is created if absent,
// and OneSignal dedupes by external_id so repeat signups update one user. Tags are
// optional segmentation data and are omitted on the retry when the org's tag-key
// limit is reached (see the handler below).
async function upsertEmailSubscriber(
  appId: string,
  restApiKey: string,
  externalId: string,
  email: string,
  language: string,
  tags?: Record<string, string>,
): Promise<Response> {
  const properties: Record<string, unknown> = { language };
  if (tags) properties.tags = tags;
  return fetch(`https://api.onesignal.com/apps/${appId}/users`, {
    method: "POST",
    headers: {
      Authorization: `Key ${restApiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      identity: { external_id: externalId },
      subscriptions: [{ type: "Email", token: email, enabled: true, notification_types: 1 }],
      properties,
    }),
  });
}

export const submitWaitlist = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => inputSchema.parse(data))
  .handler(async ({ data }) => {
    const appId = process.env.ONESIGNAL_APP_ID;
    const restApiKey = process.env.ONESIGNAL_REST_API_KEY;
    if (!appId || !restApiKey) {
      return { ok: false as const, error: "Service not configured" };
    }

    // Anti-spam: verify the Cloudflare Turnstile token (when configured) before
    // creating a subscriber, so bots can't flood the OneSignal list.
    const turnstileSecret = process.env.TURNSTILE_SECRET_KEY;
    if (turnstileSecret) {
      const { getRequestIP } = await import("@tanstack/react-start/server");
      const remoteip = getRequestIP({ xForwardedFor: true }) ?? undefined;
      const verifyBody = new URLSearchParams();
      verifyBody.set("secret", turnstileSecret);
      verifyBody.set("response", data.captchaToken);
      if (remoteip) verifyBody.set("remoteip", remoteip);
      const verifyRes = await fetch(
        "https://challenges.cloudflare.com/turnstile/v0/siteverify",
        { method: "POST", body: verifyBody },
      );
      const verifyJson = (await verifyRes.json().catch(() => ({ success: false }))) as {
        success: boolean;
        "error-codes"?: string[];
      };
      if (!verifyJson.success) {
        console.error("waitlist turnstile failed:", verifyJson["error-codes"]);
        return { ok: false as const, error: "Verification failed. Please try again." };
      }
    }

    // Geo enrichment for segmentation tags. The IP is used only transiently for
    // the country lookup and is never stored.
    const ip = extractIp();
    const usableIp = ip && !isPrivateIp(ip) ? ip : null;
    let country = await lookupCountry(usableIp);
    const language = data.browserLanguage || extractHeaderLanguage() || data.siteLanguage;
    if (!country.name && !country.code) {
      country = countryFromBrowserLanguage(language) || country;
    }

    const email = data.email.trim().toLowerCase();
    // OneSignal's Free plan allows only 2 data tags per user, so keep the set to
    // `source` + `country_code`. `language` rides along as a first-class OneSignal
    // property (below), not a data tag, and the human-readable country name is
    // dropped as redundant with country_code.
    const tags: Record<string, string> = { source: "waitlist" };
    if (country.code) tags.country_code = country.code;

    const externalId = await emailExternalId(email);
    let res = await upsertEmailSubscriber(
      appId,
      restApiKey,
      externalId,
      email,
      data.siteLanguage,
      tags,
    );
    let body = await res.text().catch(() => "");

    // OneSignal caps how many distinct tag keys the org's plan can store and rejects
    // the whole upsert (409 entitlements-tag-limit) once that cap is reached. The
    // email is the essential data, so drop the segmentation tags and retry rather
    // than lose the signup.
    if (res.status === 409 && body.includes("entitlements-tag-limit")) {
      console.warn("waitlist onesignal tag limit reached, retrying without tags");
      res = await upsertEmailSubscriber(appId, restApiKey, externalId, email, data.siteLanguage);
      body = await res.text().catch(() => "");
    }

    // 200/201 = created, 202 = existing user modified — all success.
    if (!res.ok) {
      console.error("waitlist onesignal failed", res.status, body.slice(0, 500));
      return { ok: false as const, error: "Could not save your email. Please try again." };
    }

    return { ok: true as const };
  });
